import React, { useState } from 'react';
import { Button, toast } from '@ncoder/ui';
import { GitBranch, Github, Settings } from 'lucide-react';
import { RepositoryStatusCard, RepositoryInfo } from '@/components/atoms/version-control/RepositoryStatusCard';
import { VersionControlTabs } from '@/components/organisms/version-control/VersionControlTabs';
import { CommitData } from '@/components/atoms/version-control/CommitCard';
import { BranchData } from '@/components/atoms/version-control/BranchCard';
import { ReleaseData } from '@/components/atoms/version-control/ReleaseCard';

// Mock data
const repositoryInfo: RepositoryInfo = {
  name: 'ncoder-ai-ide',
  owner: 'your-org',
  branch: 'main',
  lastSync: '2 minutes ago',
  status: 'synced',
  url: 'https://github.com/your-org/ncoder-ai-ide'
};

const branches: BranchData[] = [
  { name: 'main', isDefault: true, lastCommit: '2 hours ago', author: 'John Doe', commits: 142, status: 'active' },
  { name: 'feature/api-improvements', isDefault: false, lastCommit: '1 day ago', author: 'Jane Smith', commits: 8, status: 'active' },
  { name: 'hotfix/security-patch', isDefault: false, lastCommit: '3 days ago', author: 'Mike Johnson', commits: 3, status: 'merged' },
  { name: 'develop', isDefault: false, lastCommit: '1 week ago', author: 'Sarah Wilson', commits: 97, status: 'active' }
];

const commits: CommitData[] = [
  {
    id: '7f3a8b9',
    message: 'feat: Add advanced SOQL query builder with syntax highlighting',
    author: 'John Doe',
    date: '2 hours ago',
    branch: 'main',
    files: 8,
    additions: 156,
    deletions: 23
  },
  {
    id: '9e2c4d1',
    message: 'fix: Resolve authentication token refresh issue',
    author: 'Jane Smith',
    date: '1 day ago',
    branch: 'feature/api-improvements',
    files: 3,
    additions: 45,
    deletions: 12
  },
  {
    id: '1a5f6c8',
    message: 'docs: Update deployment documentation with new CI/CD pipeline',
    author: 'Mike Johnson',
    date: '2 days ago',
    branch: 'main',
    files: 2,
    additions: 89,
    deletions: 5
  },
  {
    id: '6b8d3e2',
    message: 'refactor: Optimize metadata parsing performance',
    author: 'Sarah Wilson',
    date: '3 days ago',
    branch: 'develop',
    files: 12,
    additions: 234,
    deletions: 78
  }
];

const tags: ReleaseData[] = [
  { name: 'v2.1.0', commit: '7f3a8b9', date: '1 week ago', description: 'Major feature release with SOQL improvements' },
  { name: 'v2.0.5', commit: '9e2c4d1', date: '2 weeks ago', description: 'Security patches and bug fixes' },
  { name: 'v2.0.4', commit: '1a5f6c8', date: '1 month ago', description: 'Performance optimizations' }
];

export const VersionControl: React.FC = () => {
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnectGitHub = async () => {
    setIsConnecting(true);
    setTimeout(() => {
      setIsConnecting(false);
      toast({ title: "Connected to GitHub", description: "Repository sync is now active" });
    }, 2000);
  };

  const handleViewCommitDetails = (commitId: string) => {
    toast({ title: "View Commit", description: `Viewing details for commit ${commitId}` });
  };

  const handleNewCommit = (message: string, branch: string) => {
    toast({ title: "Changes committed", description: `Committed to ${branch}: ${message}` });
  };

  const handleSwitchBranch = (branchName: string) => {
    toast({ title: "Switched branch", description: `Switched to branch ${branchName}` });
  };

  const handleMergeBranch = (branchName: string) => {
    toast({ title: "Branch merged", description: `Merged ${branchName} successfully` });
  };

  const handleCreatePR = (branchName: string) => {
    toast({ title: "Pull Request", description: `Creating PR for ${branchName}` });
  };

  const handleCreateBranch = (branchName: string, fromBranch: string) => {
    toast({ title: "Branch created", description: `Created ${branchName} from ${fromBranch}` });
  };

  const handleDownloadRelease = (releaseName: string) => {
    toast({ title: "Download", description: `Downloading ${releaseName}` });
  };

  const handleViewReleaseNotes = (releaseName: string) => {
    toast({ title: "Release Notes", description: `Viewing notes for ${releaseName}` });
  };

  const handleCreateRelease = () => {
    toast({ title: "Create Release", description: "Opening release creation dialog" });
  };

  const handlePush = () => {
    toast({ title: "Pushing changes", description: "Pushing local commits to remote repository" });
  };

  const handlePull = () => {
    toast({ title: "Pulling changes", description: "Pulling latest changes from remote repository" });
  };

  const handleSync = () => {
    toast({ title: "Syncing", description: "Synchronizing with remote repository" });
  };

  const handleResolveConflicts = () => {
    toast({ title: "Conflicts", description: "Opening conflict resolution view" });
  };

  const handleOpenUrl = () => {
    window.open(repositoryInfo.url, '_blank');
  };

  return (
    <div className="w-full max-w-none space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-page-title flex items-center gap-2">
            <GitBranch className="h-8 w-8 text-primary" />
            Version Control
          </h1>
          <p className="text-subtitle">Manage your code repository, branches, and deployments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button onClick={handleConnectGitHub} disabled={isConnecting}>
            <Github className="h-4 w-4 mr-2" />
            {isConnecting ? 'Connecting...' : 'Connect GitHub'}
          </Button>
        </div>
      </div>

      {/* Repository Status Card */}
      <RepositoryStatusCard
        repository={repositoryInfo}
        onOpenUrl={handleOpenUrl}
      />

      {/* Version Control Tabs */}
      <VersionControlTabs
        commits={commits}
        branches={branches}
        releases={tags}
        onViewCommitDetails={handleViewCommitDetails}
        onNewCommit={handleNewCommit}
        onSwitchBranch={handleSwitchBranch}
        onMergeBranch={handleMergeBranch}
        onCreatePR={handleCreatePR}
        onCreateBranch={handleCreateBranch}
        onDownloadRelease={handleDownloadRelease}
        onViewReleaseNotes={handleViewReleaseNotes}
        onCreateRelease={handleCreateRelease}
        onPush={handlePush}
        onPull={handlePull}
        onSync={handleSync}
        onResolveConflicts={handleResolveConflicts}
      />
    </div>
  );
};

export default VersionControl;